import { useCallback, useEffect, useMemo, useState } from 'react';
import { useProfileFields } from 'shared/hooks/profile-fields.hooks';
import workspacesAPI from '@/api/workspaces.api';
import { getLabels } from '../../../components/upload-contexts/api-helper';
import {
  ITEM_PATHS,
  PROFILE_FIELD_KEY_BY_USER_PATH,
  STATIC_VOCABULARIES,
  USER_PATHS,
  VALUE_SOURCE_BY_ITEM_PATH,
  tagDisplayName,
} from './constants';

/** Flatten the grouped GET /profile-fields response into `{ [fieldKey]: field }`. */
const buildFieldsByKey = byGroup => {
  const all = byGroup ? [...(byGroup.ORGANIZATIONAL ?? []), ...(byGroup.PROFESSIONAL ?? [])] : [];
  return all.reduce((acc, field) => {
    acc[field.fieldKey] = field;
    return acc;
  }, {});
};

/**
 * Every picker a ranking rule needs, sourced from the organization currently
 * switched into by `organization.context` — labels, workspaces and the org's own
 * profile fields. Nothing here takes an org id: the switched token scopes it.
 */
export function useRankingRuleOptions() {
  const [labels, setLabels] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loadingOrgData, setLoadingOrgData] = useState(true);
  const [orgDataError, setOrgDataError] = useState(null);

  const {
    data: profileFieldsByGroup,
    isLoading: loadingProfileFields,
    error: profileFieldsError,
  } = useProfileFields();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [labelsResult, workspacesResponse] = await Promise.all([
          getLabels(),
          workspacesAPI.getWorkspaces(),
        ]);
        if (cancelled) return;
        if (workspacesResponse.error) throw workspacesResponse.error;
        setLabels(labelsResult ?? []);
        setWorkspaces(workspacesResponse.data ?? []);
      } catch (err) {
        if (!cancelled) setOrgDataError(err);
      } finally {
        if (!cancelled) setLoadingOrgData(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const profileFieldsByKey = useMemo(
    () => buildFieldsByKey(profileFieldsByGroup),
    [profileFieldsByGroup],
  );

  const labelOptions = useMemo(
    () =>
      labels.map(label => ({
        value: label.code,
        label: label.name,
        hint: label.code,
        search: `${label.name} ${label.code}`,
      })),
    [labels],
  );

  const workspaceOptions = useMemo(
    () => workspaces.map(workspace => ({ value: workspace.id, label: workspace.name })),
    [workspaces],
  );

  const staticOptions = useMemo(() => {
    const toOptions = (codes, withDisplayName) =>
      codes.map(code => ({
        value: code,
        label: withDisplayName ? tagDisplayName(code) : code,
        hint: withDisplayName ? code : undefined,
        search: withDisplayName ? `${tagDisplayName(code)} ${code}` : code,
      }));
    return {
      entityTypes: toOptions(STATIC_VOCABULARIES.entityTypes, false),
      specialtyTags: toOptions(STATIC_VOCABULARIES.specialtyTags, true),
      contentTypeTags: toOptions(STATIC_VOCABULARIES.contentTypeTags, true),
      targetAudienceTags: toOptions(STATIC_VOCABULARIES.targetAudienceTags, true),
    };
  }, []);

  const valueOptionsFor = useCallback(
    itemPath => {
      const source = VALUE_SOURCE_BY_ITEM_PATH[itemPath];
      return source === 'labels' ? labelOptions : (staticOptions[source] ?? []);
    },
    [labelOptions, staticOptions],
  );

  /**
   * Only fields this organization has actually configured are offered — a rule
   * pointing at an unconfigured field is accepted by the backend and silently
   * never fires, so it must not be reachable from the form.
   */
  const userPathOptions = useMemo(() => {
    const options = [];
    if (labels.length) {
      options.push({ value: USER_PATHS.LOCATION, label: 'Location', hint: 'Organization labels' });
    }
    Object.entries(PROFILE_FIELD_KEY_BY_USER_PATH).forEach(([userPath, fieldKey]) => {
      const field = profileFieldsByKey[fieldKey];
      if (!field?.options?.length) return;
      options.push({
        value: userPath,
        label: field.displayLabel,
        hint: `${field.options.length} option${field.options.length === 1 ? '' : 's'}`,
      });
    });
    return options;
  }, [labels, profileFieldsByKey]);

  const userPathLabel = useCallback(
    userPath => {
      if (userPath === USER_PATHS.LOCATION) return 'Location';
      const fieldKey = PROFILE_FIELD_KEY_BY_USER_PATH[userPath];
      return profileFieldsByKey[fieldKey]?.displayLabel ?? fieldKey ?? userPath;
    },
    [profileFieldsByKey],
  );

  const workspaceLabel = useCallback(
    id => workspaces.find(workspace => workspace.id === id)?.name ?? id,
    [workspaces],
  );

  const valueLabel = useCallback(
    (itemPath, value) => {
      if (itemPath === ITEM_PATHS.LABELS) {
        return labels.find(label => label.code === value)?.name ?? value;
      }
      return value;
    },
    [labels],
  );

  return {
    labels,
    labelOptions,
    workspaces,
    workspaceOptions,
    workspaceLabel,
    profileFieldsByKey,
    userPathOptions,
    userPathLabel,
    valueOptionsFor,
    valueLabel,
    loading: loadingOrgData || loadingProfileFields,
    error: orgDataError || profileFieldsError,
  };
}
